/**
 * /invite/<invite_id>
 *
 * Public landing page for an employee accepting an invitation generated
 * by SU/Admin. Flow:
 *   1. Read invite_id from URL path.
 *   2. GET /api/auth/invite-info to confirm the invite is still valid
 *      and surface the email + role + expiry to the user.
 *   3. Collect: full name + master password (12+) + confirm.
 *   4. Bootstrap a fresh keypair client-side (same primitive used at
 *      genesis): Argon2id KEK -> ASK -> ECDH P-256 -> wrap_priv +
 *      wrap_metadata_key. Sign own pubkey self-attestation placeholder
 *      (SU will re-attest on first vault item wrap).
 *   5. POST /api/auth/invite-accept with the full bootstrap payload.
 *   6. Server creates the user row, consumes the invite, issues session
 *      cookie. SPA persists the unlocked material and drops the user
 *      into the vault.
 */

import { html, nothing, type TemplateResult } from "lit-html";

import { api, ApiError } from "../api.js";
import { bootstrapGenesisLocal, persistUnlocked } from "../client-crypto.js";
import { setRoute, setUser, showToast, state as appState } from "../main.js";

const s = {
  loaded: false,
  invite: null as null | { invite_id: string; email: string; role: string; expires_at: number; has_preseed: boolean },
  err: "",
  name: "",
  password: "",
  password2: "",
  busy: false,
  inviteId: "",
};

function inviteIdFromUrl(): string {
  // Path looks like /invite/<ulid>
  const m = /^\/invite\/([^/?#]+)/.exec(window.location.pathname);
  return m?.[1] ?? "";
}

async function loadInvite(): Promise<void> {
  if (s.loaded) return;
  s.loaded = true;
  s.inviteId = inviteIdFromUrl();
  if (!s.inviteId) {
    s.err = appState.lang === "en" ? "Missing invite id in URL" : "URL に招待 ID がありません";
    return;
  }
  try {
    s.invite = await api.inviteInfo(s.inviteId);
  } catch (err) {
    s.err = err instanceof ApiError ? err.message : (err as Error).message;
  }
}

async function submit(): Promise<void> {
  if (s.busy || !s.invite) return;
  s.err = "";
  if (!s.name.trim()) {
    s.err = appState.lang === "en" ? "Full name is required" : "氏名は必須です";
    return;
  }
  if (s.password.length < 12) {
    s.err = appState.lang === "en" ? "Password must be at least 12 chars" : "パスワードは 12 文字以上必要です";
    return;
  }
  if (s.password !== s.password2) {
    s.err = appState.lang === "en" ? "Passwords do not match" : "パスワードが一致しません";
    return;
  }
  s.busy = true;
  try {
    // Reuse the genesis-bootstrap helper (the keypair shape is identical
    // for SU and employee; only the pubkey signature is treated specially
    // by the server — employee invites get a placeholder pubkey_sig that
    // the SU re-attests when they next wrap a vault item to this user).
    const boot = await bootstrapGenesisLocal({
      email: s.invite.email,
      personal_email: s.invite.email,
      name: s.name,
      password: s.password,
      role: s.invite.role as "ADMIN" | "EMPLOYEE",
    });

    const body: Record<string, unknown> = {
      invite_id: s.invite.invite_id,
      email: s.invite.email,
      name: s.name,
      personal_email: s.invite.email,
      kdf_algo: boot.body.kdf_algo,
      kdf_params_json: boot.body.kdf_params_json,
      kdf_salt_b64: boot.body.kdf_salt_b64,
      ecdh_pub_jwk: boot.body.ecdh_pub_jwk,
      wrapped_priv_b64: boot.body.wrapped_priv_b64,
      wrapped_ask_b64: boot.body.wrapped_ask_b64,
      wrapped_metadata_key_b64: boot.body.wrapped_metadata_key_b64,
      pubkey_sig_b64: boot.body.pubkey_sig_b64,
      master_password_hash_b64: boot.body.master_password_hash_b64,
    };

    const resp = await api.inviteAccept(body);
    (window as unknown as { __unlocked?: unknown }).__unlocked = boot.unlocked;
    persistUnlocked(boot.unlocked);
    setUser({
      id: resp.user.id as string,
      email: resp.user.email as string,
      name: s.name,
      role: resp.user.role as string,
    });
    showToast(appState.lang === "en" ? "Welcome to Omakase Vault" : "Omakase Vault へようこそ");
    // Clear the path so the user sees /vault on next navigation
    history.replaceState({}, "", "/");
    setRoute("vault");
  } catch (err) {
    s.err = err instanceof ApiError ? err.message : (err as Error).message;
  } finally {
    s.busy = false;
  }
}

export function renderInvite(): TemplateResult {
  void loadInvite();
  if (s.err && !s.invite) {
    return html`
      <div class="login-shell">
        <div class="login-card">
          <h1>
            <span data-lang="en">Invitation invalid</span><span data-lang="jp">招待が無効です</span>
          </h1>
          <p style="color: var(--status-error); font-size: 13px;">${s.err}</p>
          <p style="font-size: 13px; color: var(--text-2);">
            <span data-lang="en">Ask your administrator for a new invitation link.</span><span data-lang="jp">管理者に新しい招待リンクを依頼してください。</span>
          </p>
        </div>
      </div>
    `;
  }
  if (!s.invite) {
    return html`
      <div class="login-shell">
        <div class="login-card">
          <p>
            <span data-lang="en">Verifying invitation…</span><span data-lang="jp">招待を確認中…</span>
          </p>
        </div>
      </div>
    `;
  }
  const expiryStr = new Date(s.invite.expires_at).toLocaleString(appState.lang === "en" ? "en-GB" : "ja-JP");
  return html`
    <div class="login-shell">
      <div class="login-card">
        <h1 style="text-align: center;">
          <span class="brand-wordmark" style="font-size: 26px;">
            <span class="word-omakase">O<span class="first-a">M</span>AKASE</span>
            <span class="word-vault">Vault</span>
          </span>
        </h1>
        <p style="text-align: center; font-size: 13px; color: var(--text-2); margin-bottom: 16px;">
          <span data-lang="en">You have been invited to join as <b>${s.invite.role}</b>.</span><span data-lang="jp"><b>${s.invite.role}</b> として参加するよう招待されました。</span>
        </p>
        <div class="form-row">
          <label><span data-lang="en">Email</span><span data-lang="jp">メールアドレス</span></label>
          <input type="email" .value=${s.invite.email} disabled />
        </div>
        <div class="form-row">
          <label><span data-lang="en">Full name</span><span data-lang="jp">氏名</span></label>
          <input type="text" autocomplete="name" .value=${s.name}
            @input=${(e: InputEvent) => (s.name = (e.target as HTMLInputElement).value)} />
        </div>
        <div class="form-row">
          <label><span data-lang="en">Master password (12+ chars)</span><span data-lang="jp">マスターパスワード (12 文字以上)</span></label>
          <input type="password" autocomplete="new-password" .value=${s.password}
            @input=${(e: InputEvent) => (s.password = (e.target as HTMLInputElement).value)} />
        </div>
        <div class="form-row">
          <label><span data-lang="en">Confirm password</span><span data-lang="jp">パスワード確認</span></label>
          <input type="password" autocomplete="new-password" .value=${s.password2}
            @input=${(e: InputEvent) => (s.password2 = (e.target as HTMLInputElement).value)} />
        </div>
        ${s.err ? html`<p style="color: var(--status-error); font-size: 13px;">${s.err}</p>` : nothing}
        <button class="primary" style="width: 100%; margin-top: 8px;" ?disabled=${s.busy}
          @click=${() => void submit()}>
          ${s.busy
            ? html`<span data-lang="en">Creating account…</span><span data-lang="jp">アカウント作成中…</span>`
            : html`<span data-lang="en">Accept invitation</span><span data-lang="jp">招待を承認</span>`}
        </button>
        <p style="text-align: center; font-size: 11px; color: var(--text-2); margin-top: 12px;">
          <span data-lang="en">Invitation expires ${expiryStr}</span><span data-lang="jp">招待有効期限: ${expiryStr}</span>
        </p>
      </div>
    </div>
  `;
}
